import React, { useRef, useEffect, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";

function InteractiveParticles({ count = 100, dark, interactive = true }) {
  const particles = useRef();
  const [mouse, setMouse] = useState([0, 0]);
  const [hovered, setHovered] = useState(null);
  
  useEffect(() => {
    if (!interactive) return;
    
    const handleMouseMove = (event) => {
      setMouse([
        (event.clientX / window.innerWidth) * 2 - 1,
        -(event.clientY / window.innerHeight) * 2 + 1,
      ]);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [interactive]);

  useFrame(({ clock }) => {
    if (particles.current) {
      particles.current.children.forEach((particle, i) => {
        const time = clock.getElapsedTime();
        const baseX = Math.sin(time * 0.3 + i * 0.1) * 10;
        const baseY = Math.cos(time * 0.2 + i * 0.1) * 6;
        const baseZ = Math.sin(time * 0.4 + i * 0.1) * 3;
        
        if (interactive) {
          // Add mouse interaction
          const mouseInfluence = 2;
          particle.position.x = baseX + mouse[0] * mouseInfluence;
          particle.position.y = baseY + mouse[1] * mouseInfluence;
          particle.position.z = baseZ;
        } else {
          particle.position.x = baseX;
          particle.position.y = baseY;
          particle.position.z = baseZ;
        }
        
        particle.rotation.z = time * 0.5 + i;
        particle.rotation.x = time * 0.3 + i;
        
        // Pulse effect
        const scale = 1 + Math.sin(time * 2 + i) * 0.2;
        particle.scale.setScalar(scale);
      });
    }
  });

  const particleColors = dark 
    ? ["#a5b4fc", "#818cf8", "#6366f1", "#4f46e5", "#e6e9f5"]
    : ["#1b1c4e", "#23244d", "#3730a3", "#4338ca", "#6366f1"];

  return (
    <group ref={particles}>
      {Array.from({ length: count }).map((_, i) => (
        <mesh 
          key={i} 
          position={[
            (Math.random() - 0.5) * 20,
            (Math.random() - 0.5) * 12,
            (Math.random() - 0.5) * 6
          ]}
          onPointerOver={() => setHovered(i)}
          onPointerOut={() => setHovered(null)}
        >
          <boxGeometry args={[0.03, 0.03, 0.03]} />
          <meshBasicMaterial 
            color={particleColors[i % particleColors.length]}
            opacity={hovered === i ? 0.8 : 0.4} 
            transparent 
          />
        </mesh>
      ))}
    </group>
  );
}

function FloatingOrbs({ count = 8, dark }) {
  const orbs = useRef();
  
  useFrame(({ clock }) => {
    if (orbs.current) {
      orbs.current.children.forEach((orb, i) => {
        const time = clock.getElapsedTime();
        orb.position.x = Math.sin(time * 0.2 + i) * 15;
        orb.position.y = Math.cos(time * 0.15 + i) * 8;
        orb.position.z = Math.sin(time * 0.3 + i) * 4;
        orb.rotation.y = time * 0.5 + i;
        orb.rotation.x = time * 0.3 + i;
      });
    }
  });

  return (
    <group ref={orbs}>
      {Array.from({ length: count }).map((_, i) => (
        <mesh key={i} position={[
          (Math.random() - 0.5) * 30,
          (Math.random() - 0.5) * 16,
          (Math.random() - 0.5) * 8
        ]}>
          <sphereGeometry args={[0.1 + Math.random() * 0.1, 16, 16]} />
          <meshBasicMaterial 
            color={dark ? "#a5b4fc" : "#1b1c4e"}
            opacity={0.2} 
            transparent 
          />
        </mesh>
      ))}
    </group>
  );
}

export default function ParticleSystem({ dark, interactive = true, className = "" }) {
  return (
    <div className={`fixed inset-0 pointer-events-none ${className}`} style={{ zIndex: 5 }}>
      <Canvas camera={{ position: [0, 0, 8], fov: 60 }}>
        <ambientLight intensity={0.3} />
        <InteractiveParticles count={80} dark={dark} interactive={interactive} />
        <FloatingOrbs count={6} dark={dark} />
      </Canvas>
    </div>
  );
}

export function SectionParticles({ dark, count = 30, className = "" }) {
  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
        <ambientLight intensity={0.4} />
        <InteractiveParticles count={count} dark={dark} interactive={false} />
      </Canvas>
    </div>
  );
} 